"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { apiFetch } from "@/lib/api-client";
import { ContentSkeleton } from "@/components/site/content-skeleton";

type PromptItem = {
  id: string;
  name: string;
  description: string;
  tags: string[];
  groupName: string;
};

export type PickedPrompt = {
  id: string;
  name: string;

  body: string;

  variables: string[];
};

type Props = {
  disabled?: boolean;
  onPick: (prompt: PickedPrompt) => void;
};

export function AiPromptPicker({ disabled, onPick }: Props) {
  const t = useTranslations("member.promptPicker");
  const ui = useTranslations("site.ui");
  const [listError, setListError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<PromptItem[] | null>(null);
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const pickRequest = useRef<AbortController | null>(null);
  useEffect(() => () => pickRequest.current?.abort(), []);

  const loadList = useCallback(async (signal: AbortSignal) => {
    setLoading(true);
    setListError(null);

    try {
      const result = await apiFetch<{ prompts: PromptItem[] }>(
        "/api/member/ai/prompt",
        { signal },
      );
      if (signal.aborted) return;

      setItems(result.prompts);
    } catch (error) {
      if (signal.aborted) return;
      const message = error instanceof Error ? error.message : t("loadFailed");
      setListError(message);
      toast.error(message);
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!open || items !== null) return;
    const controller = new AbortController();
    queueMicrotask(() => { if (!controller.signal.aborted) void loadList(controller.signal); });
    return () => controller.abort();
  }, [open, items, loadList, reload]);

  const close = () => {
    pickRequest.current?.abort();
    setLoading(false);
    setOpen(false);
    setKeyword("");
  };

  const pick = async (id: string) => {
    pickRequest.current?.abort();
    const controller = new AbortController();
    pickRequest.current = controller;
    setLoading(true);

    try {
      const result = await apiFetch<PickedPrompt>(
        `/api/member/ai/prompt/${id}`,
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;

      onPick(result);
      close();
    } catch (error) {
      if (controller.signal.aborted) return;
      toast.error(error instanceof Error ? error.message : t("readFailed"));
    } finally {
      if (pickRequest.current === controller && !controller.signal.aborted) setLoading(false);
    }
  };

  const word = keyword.trim().toLowerCase();
  const filtered = (items ?? []).filter((item) =>
    word
      ? [item.name, item.description, ...item.tags]
          .join(" ")
          .toLowerCase()
          .includes(word)
      : true,
  );

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-8 gap-1.5 text-xs"
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        <Sparkles className="size-3.5" />
        {t("button")}
      </Button>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) close();
        }}
      >
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("title")}</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <Input
              value={keyword}
              placeholder={t("searchPlaceholder")}
              onChange={(event) => setKeyword(event.target.value)}
            />

            <div className="max-h-[50vh] space-y-2 overflow-auto">
              {items === null ? (
                listError ? <div role="alert" className="text-sm text-destructive">{listError}<Button variant="link" onClick={() => setReload(value => value + 1)}>{ui("retry")}</Button></div> : <ContentSkeleton variant="rows" label={t("loading")} />
              ) : filtered.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  {items?.length
                    ? t("noMatch")
                    : t("empty")}
                </p>
              ) : (
                filtered.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    disabled={loading}
                    className="w-full rounded-md border p-3 text-left transition-colors hover:bg-accent disabled:opacity-60"
                    onClick={() => void pick(item.id)}
                  >
                    <span className="block text-sm font-medium">
                      {item.name}
                      {item.groupName ? (
                        <span className="ml-2 text-xs font-normal text-muted-foreground">
                          {item.groupName}
                        </span>
                      ) : null}
                    </span>
                    {item.description ? (
                      <span className="mt-0.5 block line-clamp-2 text-xs text-muted-foreground">
                        {item.description}
                      </span>
                    ) : null}
                  </button>
                ))
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={close}>
              {t("cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
